import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  Checkbox,
  Chip,
  Spinner,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { RetailPrice } from 'common/src/utils/types';
import { updateRetailPrice } from 'common/src/api/retail_skus';
import {
  RETAIL_UNIT_OPTIONS,
  convertToKg,
  computeUsdPerKg,
  fetchRetailFxRate,
} from 'common/src/utils/retailPriceCalc';
import { useConfirm } from '../../../components/ui';

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

const ORIGIN_OPTIONS = [
  { key: '', label: '— Sin especificar —' },
  { key: 'CL', label: 'Chile (CL)' },
  { key: 'PE', label: 'Perú (PE)' },
  { key: 'MX', label: 'México (MX)' },
  { key: 'NZ', label: 'Nueva Zelanda (NZ)' },
  { key: 'IT', label: 'Italia (IT)' },
  { key: 'ES', label: 'España (ES)' },
  { key: 'US', label: 'Estados Unidos (US)' },
  { key: 'BR', label: 'Brasil (BR)' },
  { key: 'AR', label: 'Argentina (AR)' },
  { key: 'ZA', label: 'Sudáfrica (ZA)' },
];

const USD_KG_MIN = 0.1;
const USD_KG_MAX = 50;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  price: RetailPrice | null;
  onSaved: () => void;
}

const formatNum = (v: number | null | undefined, digits = 2) =>
  v == null
    ? '—'
    : v.toLocaleString(undefined, { maximumFractionDigits: digits });

// captured_at (UTC ISO) <-> <input type="datetime-local"> local string
const toLocalInput = (iso: string): string => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const EditPriceModal: React.FC<Props> = ({
  isOpen,
  onClose,
  price,
  onSaved,
}) => {
  const [priceValue, setPriceValue] = useState('');
  const confirm = useConfirm();
  const [sizeValue, setSizeValue] = useState('');
  const [sizeUnit, setSizeUnit] = useState('kg');
  const [origin, setOrigin] = useState('');
  const [onPromo, setOnPromo] = useState(false);
  const [capturedAt, setCapturedAt] = useState('');
  const [saving, setSaving] = useState(false);

  // FX: start from the row's stored rate (the day's rate). If the row has
  // none, fetch the current one so we can still compute USD/kg.
  const [fx, setFx] = useState<{ rate: number; isFallback: boolean } | null>(
    null
  );
  const [fxLoading, setFxLoading] = useState(false);

  const currency = price?.currency ?? '';
  const speciesId = price?.species_id ?? 0;

  useEffect(() => {
    if (!isOpen || !price) return;
    setPriceValue(price.price_local != null ? String(price.price_local) : '');
    setSizeValue(
      price.package_size_value != null ? String(price.package_size_value) : ''
    );
    setSizeUnit(price.package_size_unit ?? 'kg');
    setOrigin(price.origin_country ?? '');
    setOnPromo(price.on_promo);
    setCapturedAt(toLocalInput(price.captured_at));

    if (price.fx_rate_used != null) {
      setFx({ rate: price.fx_rate_used, isFallback: false });
    } else if (currency) {
      setFxLoading(true);
      fetchRetailFxRate(currency)
        .then((r) => setFx(r))
        .catch(() => setFx({ rate: 1, isFallback: true }))
        .finally(() => setFxLoading(false));
    } else {
      setFx(null);
    }
  }, [isOpen, price, currency]);

  const priceNum = parseFloat(priceValue);
  const sizeNum = parseFloat(sizeValue);

  const preview = useMemo(() => {
    if (!price) return null;
    if (!Number.isFinite(priceNum) || !Number.isFinite(sizeNum)) return null;
    const conv = convertToKg(sizeNum, sizeUnit, speciesId);
    const usdKg = computeUsdPerKg(
      priceNum,
      currency,
      conv.weight_kg,
      fx?.rate ?? null
    );
    return { ...conv, usdKg };
  }, [price, priceNum, sizeNum, sizeUnit, fx, currency, speciesId]);

  // Units list — keep the row's current unit even if it's not in the
  // standard manual-entry options (e.g. a scraped 'stuks').
  const unitOptions = useMemo(() => {
    const base = RETAIL_UNIT_OPTIONS.map((o) => ({ key: o.key, label: o.label }));
    if (sizeUnit && !base.some((o) => o.key === sizeUnit)) {
      base.unshift({ key: sizeUnit, label: sizeUnit });
    }
    return base;
  }, [sizeUnit]);

  if (!price) return null;

  const priceOk = Number.isFinite(priceNum) && priceNum > 0;
  const sizeOk = Number.isFinite(sizeNum) && sizeNum > 0;
  const canSave = priceOk && sizeOk && !fxLoading && !saving;

  const usdKgOutOfRange =
    preview?.usdKg != null &&
    (preview.usdKg < USD_KG_MIN || preview.usdKg > USD_KG_MAX);
  const noWeight = preview != null && preview.weight_kg == null;

  const handleSave = async () => {
    if (!canSave) return;

    if (noWeight) {
      const ok = await confirm({
        title: '¿Guardar sin USD/kg?',
        message: `La unidad "${sizeUnit}" no tiene regla de conversión a kg para esta especie. Se guardará el precio pero sin USD/kg.`,
        confirmLabel: 'Guardar',
      });
      if (!ok) return;
    } else if (usdKgOutOfRange) {
      const ok = await confirm({
        title: '¿Precio fuera del rango habitual?',
        message: `El precio calculado es $${formatNum(preview?.usdKg, 2)} USD/kg, fuera del rango habitual ($${USD_KG_MIN}–$${USD_KG_MAX}). Revisa que el precio y la unidad sean correctos.`,
        confirmLabel: 'Guardar igual',
      });
      if (!ok) return;
    }

    setSaving(true);
    try {
      await updateRetailPrice(price.id, {
        priceLocal: priceNum,
        packageSizeValue: sizeNum,
        packageSizeUnit: sizeUnit,
        packageSizeText: `${sizeNum} ${sizeUnit}`,
        weightKg: preview?.weight_kg ?? null,
        weightSource: preview?.weight_source ?? null,
        priceUsdPerKg: preview?.usdKg ?? null,
        fxRateUsed: fx?.rate ?? null,
        originCountry: origin || null,
        onPromo,
        capturedAt: capturedAt ? new Date(capturedAt).toISOString() : undefined,
      });
      toast.success('Precio actualizado');
      onSaved();
      onClose();
    } catch (e: unknown) {
      toast.error(`Error guardando: ${getErrorMessage(e)}`);
    } finally {
      setSaving(false);
    }
  };

  const isManual = price.entry_method === 'manual';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='lg' scrollBehavior='inside'>
      <ModalContent>
        <ModalHeader className='flex flex-col gap-1'>
          <div className='flex items-center gap-2'>
            <Icon icon='tabler:edit' className='text-primary-500' />
            <span>Editar precio registrado</span>
            <Chip
              size='sm'
              variant='flat'
              color={isManual ? 'primary' : 'default'}
            >
              {isManual ? 'Manual' : 'Scrapeado'}
            </Chip>
          </div>
          <p className='text-sm font-normal text-default-500'>
            {price.retailer} · {price.country} · {price.product_name}
          </p>
        </ModalHeader>

        <ModalBody>
          <div className='space-y-4'>
            {!isManual && (
              <div className='rounded-lg bg-warning-50 border border-warning-200 px-3 py-2 text-sm text-warning-700'>
                Estás editando un punto <strong>scrapeado</strong>. El próximo
                scrape no lo sobrescribe (crea filas nuevas), pero la corrección
                manual queda registrada acá.
              </div>
            )}

            <div className='grid grid-cols-2 gap-3'>
              <Input
                autoFocus
                size='sm'
                type='number'
                label={`Precio (${currency})`}
                placeholder='0.00'
                value={priceValue}
                onValueChange={setPriceValue}
                isInvalid={priceValue !== '' && !priceOk}
                errorMessage={
                  priceValue !== '' && !priceOk ? 'Debe ser mayor a 0' : undefined
                }
                startContent={
                  <span className='text-default-400 text-sm'>$</span>
                }
              />
              <div className='grid grid-cols-2 gap-2'>
                <Input
                  size='sm'
                  type='number'
                  label='Cantidad'
                  placeholder='1'
                  value={sizeValue}
                  onValueChange={setSizeValue}
                  isInvalid={sizeValue !== '' && !sizeOk}
                />
                <Select
                  size='sm'
                  label='Unidad'
                  disallowEmptySelection
                  selectedKeys={new Set([sizeUnit])}
                  onSelectionChange={(keys) => {
                    const k = Array.from(keys)[0];
                    if (k) setSizeUnit(String(k));
                  }}
                >
                  {unitOptions.map((o) => (
                    <SelectItem key={o.key} value={o.key}>
                      {o.label}
                    </SelectItem>
                  ))}
                </Select>
              </div>
            </div>

            <div className='grid grid-cols-2 gap-3'>
              <Input
                size='sm'
                type='datetime-local'
                label='Fecha de captura'
                value={capturedAt}
                onValueChange={setCapturedAt}
              />
              <Select
                size='sm'
                label='Origen (opcional)'
                selectedKeys={new Set([origin])}
                onSelectionChange={(keys) => {
                  const k = Array.from(keys)[0];
                  setOrigin(k ? String(k) : '');
                }}
              >
                {ORIGIN_OPTIONS.map((o) => (
                  <SelectItem key={o.key} value={o.key}>
                    {o.label}
                  </SelectItem>
                ))}
              </Select>
            </div>

            <Checkbox
              size='sm'
              isSelected={onPromo}
              onValueChange={setOnPromo}
            >
              En promoción
            </Checkbox>

            {/* Live preview */}
            <div className='rounded-lg border border-primary-200 bg-primary-50/40 dark:bg-primary-950/20 px-4 py-3'>
              <p className='text-xs font-semibold uppercase text-primary-600 mb-2'>
                Preview (recalculado)
              </p>
              {fxLoading ? (
                <div className='flex items-center gap-2 text-sm text-default-500'>
                  <Spinner size='sm' /> Cargando tipo de cambio...
                </div>
              ) : (
                <div className='grid grid-cols-3 gap-3 text-sm'>
                  <div>
                    <p className='text-default-500 text-xs'>Peso normalizado</p>
                    <p className='font-semibold'>
                      {preview?.weight_kg != null
                        ? `${formatNum(preview.weight_kg, 4)} kg`
                        : '—'}
                    </p>
                  </div>
                  <div>
                    <p className='text-default-500 text-xs'>USD / kg</p>
                    <p
                      className={`font-semibold ${
                        usdKgOutOfRange ? 'text-warning-600' : ''
                      }`}
                    >
                      {preview?.usdKg != null
                        ? `$${formatNum(preview.usdKg, 2)}`
                        : '—'}
                    </p>
                  </div>
                  <div>
                    <p className='text-default-500 text-xs'>
                      Dólar usado{fx?.isFallback ? ' (aprox)' : ''}
                    </p>
                    <p className='font-semibold'>
                      {fx ? `${formatNum(fx.rate, 4)} ${currency}` : '—'}
                    </p>
                  </div>
                </div>
              )}
              {noWeight && (
                <p className='mt-2 text-xs text-warning-600'>
                  ⚠️ La unidad seleccionada no convierte a kg para esta especie —
                  se guardará sin USD/kg.
                </p>
              )}
              {usdKgOutOfRange && !noWeight && (
                <p className='mt-2 text-xs text-warning-600'>
                  ⚠️ El USD/kg está fuera del rango habitual ($
                  {USD_KG_MIN}–${USD_KG_MAX}). Revisa precio y unidad.
                </p>
              )}
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button variant='light' onClick={onClose} isDisabled={saving}>
            Cancelar
          </Button>
          <Button
            color='primary'
            onClick={handleSave}
            isLoading={saving}
            isDisabled={!canSave}
          >
            Guardar cambios
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditPriceModal;
